import { driverInputUnit } from '../driverUtils';
import { materialize } from '../engine/scurve';
import type { DriverType, History, Recipe, SchemaEntry } from '../engine/types';
import { materialise } from './recipe';
import { anchorFromHistory, resolveSeasonalityFactors } from './recipeHistory';
import type { CaseFileOneTimeItem } from './types';

export interface RebaseLineState {
    values: (number | null)[];
    params: Record<string, unknown>;
    driver: string | null;
    recipe?: Recipe | null;
}

export interface RebaseInput {
    lines: Record<string, Record<string, RebaseLineState>>;
    oneTimeItems: CaseFileOneTimeItem[];
    notes: Record<string, string>;
    projectionMonths: string[];
    lastClosedMonth?: string;
    history?: History;
    schema?: SchemaEntry[];
}

export interface RebaseResult {
    lines: Record<string, Record<string, RebaseLineState>>;
    oneTimeItems: CaseFileOneTimeItem[];
    notes: Record<string, string>;
    droppedOneTimeItems: CaseFileOneTimeItem[];
    droppedNotes: string[];
    rematerialisedRecipes: number;
}

function repeatFinal(values: (number | null)[], k: number): (number | null)[] {
    const tail = values.length > 0 ? values[values.length - 1] : null;
    const shifted = values.slice(k);
    while (shifted.length < 36) {
        shifted.push(tail ?? null);
    }
    return shifted.slice(0, 36);
}

function rematerialiseRecipeLine(
    state: RebaseLineState,
    view: string,
    lineKey: string,
    lastClosedMonth: string,
    projectionMonths: string[],
    history?: History,
    schema?: SchemaEntry[],
): RebaseLineState {
    const recipe = state.recipe;
    if (!recipe || !state.driver) {
        return state;
    }
    const driver = state.driver as DriverType;
    let nextRecipe = recipe;
    if (history && schema && recipe.anchor.basis && recipe.anchor.basis !== 'manual') {
        const historyValue = anchorFromHistory(
            recipe.anchor.basis,
            recipe.anchor.basis === 'trailing_avg_per_period'
                ? (recipe.anchor.runs ?? 3)
                : (recipe.anchor.months ?? 3),
            {
                view: view as import('../engine/types').View,
                lineKey,
                driverType: driver,
                params: state.params as import('../engine/types').DriverParams,
                history,
                schema,
                lastClosedMonth,
            },
        );
        if (historyValue != null) {
            nextRecipe = {
                ...recipe,
                anchor: { ...recipe.anchor, history_value: historyValue },
            };
        }
    }
    const seasonalityResult = resolveSeasonalityFactors(nextRecipe, {
        view: view as import('../engine/types').View,
        lineKey,
        driverType: driver,
        params: state.params as import('../engine/types').DriverParams,
        history,
        schema,
        lastClosedMonth,
        pathPrefix: `lines.${view}.${lineKey}.recipe`,
    });
    const values = materialise(nextRecipe, {
        lastClosedMonth,
        driverType: driver,
        unit: driverInputUnit(driver as Parameters<typeof driverInputUnit>[0]),
        seasonalityFactors: seasonalityResult.factors,
        projectionMonths,
    });
    return {
        ...state,
        recipe: nextRecipe,
        values,
    };
}

export function rebaseMaterialized(input: RebaseInput, k: number): RebaseResult {
    if (k < 1 || k > 3) {
        return {
            lines: input.lines,
            oneTimeItems: input.oneTimeItems,
            notes: input.notes,
            droppedOneTimeItems: [],
            droppedNotes: [],
            rematerialisedRecipes: 0,
        };
    }

    const droppedMonths = new Set(input.projectionMonths.slice(0, k));
    const droppedOneTimeItems = input.oneTimeItems.filter((item) => droppedMonths.has(item.month));
    const keptOneTimeItems = input.oneTimeItems.filter((item) => !droppedMonths.has(item.month));

    const droppedNotes: string[] = [];
    const keptNotes: Record<string, string> = {};
    for (const [key, text] of Object.entries(input.notes)) {
        const period = key.split(':').at(-1) ?? '';
        if (period === 'label') {
            keptNotes[key] = text;
            continue;
        }
        const t = Number(period);
        if (Number.isFinite(t) && t <= k) {
            droppedNotes.push(key);
            continue;
        }
        if (Number.isFinite(t) && t > k) {
            const parts = key.split(':');
            parts[parts.length - 1] = String(t - k);
            keptNotes[parts.join(':')] = text;
            continue;
        }
        keptNotes[key] = text;
    }

    const lastClosedMonth = input.lastClosedMonth ?? '';
    const newProjectionMonths = input.projectionMonths.slice(k);
    while (newProjectionMonths.length < 36) {
        newProjectionMonths.push(input.projectionMonths[input.projectionMonths.length - 1] ?? '');
    }

    let rematerialisedRecipes = 0;
    const lines: Record<string, Record<string, RebaseLineState>> = {};
    for (const [view, viewLines] of Object.entries(input.lines)) {
        lines[view] = {};
        for (const [lineKey, state] of Object.entries(viewLines)) {
            if (state.recipe && lastClosedMonth) {
                lines[view]![lineKey] = rematerialiseRecipeLine(
                    state,
                    view,
                    lineKey,
                    lastClosedMonth,
                    newProjectionMonths.slice(0, 36),
                    input.history,
                    input.schema,
                );
                rematerialisedRecipes += 1;
                continue;
            }
            const next: RebaseLineState = {
                driver: state.driver,
                params: { ...state.params },
                values: repeatFinal(state.values, k),
            };
            if (state.driver === 's_curve') {
                const midpoint = Number(state.params.midpoint);
                const start = Number(state.params.start ?? 0);
                const end = Number(state.params.end ?? 0);
                const steepness = Number(state.params.steepness ?? 1);
                const newMid = Math.max(1, midpoint - k);
                next.params = { start, end, midpoint: newMid, steepness };
                next.values = materialize({ start, end, midpoint: newMid, steepness });
            }
            lines[view]![lineKey] = next;
        }
    }

    return {
        lines,
        oneTimeItems: keptOneTimeItems,
        notes: keptNotes,
        droppedOneTimeItems,
        droppedNotes,
        rematerialisedRecipes,
    };
}
